import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Circle, MarkerClusterer, Polygon } from '@react-google-maps/api';
import { useMapStore } from '../store/mapStore';
import { STATUS } from '../types';
import LocationDetails from './LocationDetails';
import { applyFilters } from '../store/mapStore';
import { Search, LocateIcon } from 'lucide-react';
import MapControls from './MapControls';
import {
  iconSpec, clusterStyles,
  CLUSTER_GRID_SIZE,
  CLUSTER_MIN_SIZE,
  CLUSTER_MAX_ZOOM
} from '../utils/clusterConfig';
import { attemptGeo, defaultCenter } from '../utils/geo';


const Map = ({ apikey }) => {
  const libraries = ['places'];

  // Only initialize the loader if the key is available
  const { isLoaded: mapsLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apikey,
    libraries,
  });
  const [hoverLocation, setHoverLocation] = useState(null);
  const hoverTimerRef = useRef(null);

  const {
    locations,
    filteredLocations,
    selectedLocation,
    setSelectedLocation,
    searchCenter,
    searchByLatLng,
    getLocationId,
    filters,
    setFilters,
    clearFilters,
    setGlobalDeviceLocation,
    setGlobalMapCenter,
    globalSearchMode,
    setGlobalSearchMode,
    polygons,
    showNeighborhoods,
    showLeads,
    showBookmarksOnly,
    isFavorite,
  } = useMapStore();


  const [map, setMap] = useState(null);
  const [deviceLocation, setDeviceLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [showSearchHereTag, setShowSearchHereTag] = useState(false);
  const [isOffFromDeviceLocation, setIsOffFromDeviceLocation] = useState(false);
  const [polyInfo, setPolyInfo] = useState(null);


  useEffect(() => {
    attemptGeo(
      pos => {
        const fix = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() };
        setDeviceLocation(fix);
        setGlobalDeviceLocation(fix);
      },
      () => {
        const fix = { ...defaultCenter, _fallback: true, ts: Date.now() };
        setDeviceLocation(fix);
        setGlobalDeviceLocation(fix);
      }
    );
  }, []);


  useEffect(() => {
    if (deviceLocation && !searchCenter) {
      console.log("Map.jsx:seach using latlong device location")
      searchByLatLng(deviceLocation.lat, deviceLocation.lng);
    }
  }, [deviceLocation, searchByLatLng]);


  const isMobile = window.innerWidth <= 800;

  // Load initial data only once
  useEffect(() => {
    if (locations.length && filteredLocations.length === 0) {
      const filtered = applyFilters(locations, filters, showLeads);
      useMapStore.setState({ filteredLocations: filtered });
    }
  }, [locations.length, filteredLocations.length, showLeads]);
  useEffect(() => {
    if (selectedLocation?._skipRecenter) {
      console.log('[Map] skip recenter for stack member selection');
      return;
    }
 
    if (map && selectedLocation && !isMobile) {
      const bounds = map.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      // Calculate center point that accounts for the UI elements
      const leftOffset = 200; // Half of map-controls width
      const rightOffset = 170; // Half of sidebar width
      const headerHeight = 100; // Header height
      const statsHeight = 76; // Stats panel height
      const gap = 25; // Gap between elements
      const topOffset = headerHeight + statsHeight + (gap * 2) + 100; // Account for header, stats, gaps and some padding

      // Convert pixel offsets to coordinate offsets
      const latOffset = (ne.lat() - sw.lat()) * (topOffset / map.getDiv().clientHeight);
      const lngOffsetLeft = (ne.lng() - sw.lng()) * (leftOffset / map.getDiv().clientWidth);
      const lngOffsetRight = (ne.lng() - sw.lng()) * (rightOffset / map.getDiv().clientWidth);

      // Calculate adjusted center
      const newCenter = {
        lat: selectedLocation.lat,
        lng: selectedLocation.lng
      };

      // Pan with a slight zoom out for better visibility
      map.setZoom(Math.max(map.getZoom() - 0.5, 15));
      map.panTo(newCenter);
    }

    if (map && selectedLocation && isMobile) {
      map.setZoom(17);
      map.panTo({ lat: selectedLocation.lat, lng: selectedLocation.lng });
    }

  }, [map, selectedLocation, isMobile]);


  useEffect(() => {
    if (!map || !searchCenter) return;

    // skip reset if 'search this area'
    const mode = useMapStore.getState().globalSearchMode;

    if (mode === 'Nearby Search') {
      map.panTo(searchCenter);
      return;
    }
    // reset zoom for all other searches
    map.setZoom(15);
    map.panTo(searchCenter);
  }, [map, searchCenter]);


  const getMarkerIcon = useCallback((location) => {
    const scale = 1;
    const size = new google.maps.Size(32 * scale, 32 * scale);
    const anchor = new google.maps.Point(16 * scale, 32 * scale);
    let business_unit = '';

    if (Number(location?._stackCount || 0) > 1) {
      return iconSpec('/markers/stacked-marker.svg'); 
    }


    if (location?.businessUnit) {
      business_unit = location.businessUnit;
    }
    else if (location?.accountDetails?.businessUnit) {
      business_unit = location.accountDetails.businessUnit;
    }
    //console.log("location business unit, location status: ", business_unit, location.shipStatus)

    const isSelected = selectedLocation && getLocationId(location).id === getLocationId(selectedLocation).id;
    // console.log('Selected Location:\n',selectedLocation,
    //   'location:\n',location,
    //   'selectedLocation ID:\n',getLocationId(selectedLocation),
    //   'location id',getLocationId(location));
    if (isSelected) {
      // If it's not US Broadline, it's considered Specialty — return early
      //console.log('BU:', location?.businessUnit, location.businessUnit);
      if ((business_unit) !== 'US Broadline' && location?.shipStatus !== 'target') {
        console.log("Selected specialty marker")
        return '/markers/selected-specialty.svg';
      }
      // console.log("selected location status: ", location.shipStatus)
      switch (location.shipStatus) {
        case STATUS.ACTIVE:
          return iconSpec('/markers/selected-active-marker.svg');
        case STATUS.ACTIVE_OLDER:
          return iconSpec('/markers/selected-activeOlder-marker.svg');
        case STATUS.PROSPECT:
          return iconSpec('/markers/selected-prospect-marker.svg');
        case STATUS.TARGET:
          return iconSpec('/markers/selected-target-marker.svg');
      }
    }

    const icons = {
      [STATUS.ACTIVE]: '/markers/active-marker.svg',
      [STATUS.ACTIVE_OLDER]: '/markers/active-older.svg',
      [STATUS.PROSPECT]: '/markers/prospect-marker.svg',
      [STATUS.TARGET]: '/markers/target-marker.svg',
    };
    const icon = ((business_unit) !== 'US Broadline' && location?.shipStatus !== 'target') ? '/markers/specialty-marker.svg' : icons[location.shipStatus] || '/markers/default-marker.svg';

    return iconSpec(icon)
  }, [selectedLocation, getLocationId]);

  //Memoize filtered locations to prevent unnecessary re-renders
  const validLocations = useMemo(() => {
    // base rows the map can render
    let rows = filteredLocations.filter(l => l?.lat && l?.lng);
    // final gate: hide Salesforce leads if toggle is OFF
    if (!showLeads) {
      const isSFLead = (r) => {
        const status = ((r.shipStatus ?? r.status) ?? '').toString().toLowerCase();
        const scope = (r.scope ?? '').toString().trim().toUpperCase();
        return status === 'target' && scope === 'SALESFORCE';
      };
      const before = rows.length;
      rows = rows.filter(r => !isSFLead(r));
      console.log('[Map] ShowLeads=false, removed', before - rows.length, 'leads; final', rows.length);
    } else {
      console.log('[Map] ShowLeads=true; rows', rows.length);
    }
    return rows;
  }, [filteredLocations, showLeads]);

  const visibleValidLocations = useMemo(() => {
    const base = Array.isArray(validLocations) ? validLocations : [];
    const out = showBookmarksOnly ? base.filter(isFavorite) : base;
    console.log('[Map] bookmarksOnly=', showBookmarksOnly, 'base=', base.length, 'visible=', out.length);
    return out;
  }, [validLocations, showBookmarksOnly, isFavorite]);

  const selectedId = getLocationId(selectedLocation)?.id;


  const validLocationsFiltered = useMemo(() => {
    const rows = Array.isArray(visibleValidLocations) ? visibleValidLocations : [];
    const kept = rows.filter(location => {
      const locId = getLocationId(location)?.id;
      const isSelected = selectedId === locId;
      const isDisplaced = location._displaced;
      return !(isSelected && isDisplaced);
    });
    console.log('[Map] after displacement-filter →', kept.length, '(from', rows.length, ')');
    return kept;
  }, [visibleValidLocations, selectedId, getLocationId]);


  const onCenterChanged = () => {
    if (map) {
      const center = map.getCenter();
      const newCenter = { lat: center.lat(), lng: center.lng() };
      setMapCenter(newCenter);
      setGlobalMapCenter(newCenter);

      const oldCenter = searchCenter;
      const dist = oldCenter ? Math.hypot(newCenter.lat - oldCenter.lat, newCenter.lng - oldCenter.lng) : 0;
      if (dist > 0.0010) setShowSearchHereTag(true);

      // Compare with device location
      if (deviceLocation) {
        const deviation = Math.hypot(newCenter.lat - deviceLocation.lat, newCenter.lng - deviceLocation.lng);
        setIsOffFromDeviceLocation(deviation > 0.0005);
      }
    }
  }

  const onDragEnd = () => {
    if (map) {
      const center = map.getCenter();
      setMapCenter({ lat: center.lat(), lng: center.lng() });
      setGlobalMapCenter({ lat: center.lat(), lng: center.lng() });
      setShowSearchHereTag(true);
    }
  }

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  if (!mapsLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const GREY_DOT = {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: '#9ca3af',          // gray-400
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#fff',
  };

  // console.log("show neighborhood: ", showNeighborhoods, polygons)
  const renderHoverHeader = (loc) => {
    const isTarget = (loc.shipStatus || loc.status)?.toLowerCase() === 'target';
    const isSalesforceLead = isTarget && loc.scope === 'SALESFORCE';

    return (
      <div className="p-1 min-w-[240px] max-w-[280px]">
        <div className="flex items-start justify-between">
          <h2 className="text-fluid-md text-zinc-700 font-bold">{loc.name}</h2>

          {/* right-side tiny badges */}
          <div className="flex gap-1">
            {isTarget && (
              <span className="px-2 py-[2px] text-[10px] rounded-full bg-purple-100 text-purple-700">
                Target
              </span>
            )}
            {isSalesforceLead && (
              <span className="px-2 py-[2px] text-[10px] rounded-full bg-amber-100 text-amber-700">
                Lead
              </span>
            )}
            {!isTarget && loc.status && (
              <span className="px-2 py-[2px] text-[10px] rounded-full bg-gray-100 text-gray-700">
                {loc.status}
              </span>
            )}
          </div>
        </div>

        <div className="mt-1 text-xs text-gray-600 truncate">
          {loc.accountDetails?.accountId ?? ''}
          {loc.accountDetails?.type ? ` • ${loc.accountDetails.type}` : ''}
        </div>

        <div className="text-[11px] text-gray-500 truncate">{loc.address}</div>
      </div>
    );
  };


  return (
    <div>
      {/* {console.log(validLocations)} */}
      <GoogleMap
        mapContainerStyle={{
          width: '100%',
          height: isMobile ? 'calc(100vh - var(--header-height))' : '100vh',
          position: 'absolute',
          inset: 0
        }}
        center={searchCenter || deviceLocation || defaultCenter}
        zoom={15}
        onLoad={setMap}
        onCenterChanged={onCenterChanged}
        onDragEnd={onDragEnd}
        onClick={() => setPolyInfo(null)}
        onDragStart={() => setHoverLocation(null)}
        onZoomChanged={() => setHoverLocation(null)}


        options={{
          minZoom: 5,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          Control: false,
          clickableIcons: false,
          gestureHandling: isMobile ? 'greedy' : 'auto',
          styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }]
        }}
      >


        {isMobile &&
          <MapControls
            showSearchHereTag={showSearchHereTag}
            mapCenter={mapCenter}
            deviceLocation={deviceLocation}
            isOffFromDeviceLocation={isOffFromDeviceLocation}
            setIsOffFromDeviceLocation={setIsOffFromDeviceLocation}
            setShowSearchHereTag={setShowSearchHereTag}
            map={map}
            setDeviceLocation={setDeviceLocation} />}

        {deviceLocation && (
          <Marker
            key={deviceLocation.ts}
            position={{ lat: deviceLocation.lat, lng: deviceLocation.lng }}
            icon={deviceLocation._fallback ? GREY_DOT : {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: 'white',
            }}
            zIndex={999}
          />
        )}


        {/* ── CLUSTERED LOCATION MARKERS ─────────────────────────────── */}
        <MarkerClusterer
          /* default Google blue badges; tweak gridSize/minimumClusterSize if desired */

          options={{
            minimumClusterSize: CLUSTER_MIN_SIZE,   // singles stay singles
            gridSize: CLUSTER_GRID_SIZE,            // px before icons merge (raise = cluster sooner)
            zoomOnClick: true,
            maxZoom: CLUSTER_MAX_ZOOM,
            averageCenter: true,       // auto-zoom /
            styles: clusterStyles

          }}
        >
          {(clusterer) =>
            validLocationsFiltered.map((location) => {
              const id = getLocationId(location);
              if (!id || !location.lat || !location.lng) return null;

              return (
                <Marker
                  key={id}
                  position={{ lat: location.lat, lng: location.lng }}
                  icon={getMarkerIcon(location)}       /* ← your SVG/PNG per-status icon */
                  onClick={() => {
                    setHoverLocation(null);
                    setSelectedLocation(location)
                  }}
                  onMouseOver={() => {
                    if (isMobile) return;         // skip on touch devices
                    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                    setHoverLocation(location);
                  }}
                  onMouseOut={() => {
                    if (isMobile) return;
                    hoverTimerRef.current = setTimeout(() => setHoverLocation(null), 120);
                  }}
                  clusterer={clusterer}                /* ← attach to marker clusterer */
                  zIndex={
                    selectedLocation &&
                      id === getLocationId(selectedLocation) ? 1000 : undefined
                  }
                />
              );
            })
          }
        </MarkerClusterer>
        {hoverLocation && !isMobile && (
          <InfoWindow
            position={{ lat: hoverLocation.lat, lng: hoverLocation.lng }}
            onCloseClick={() => setHoverLocation(null)}
            options={{ disableAutoPan: true, pixelOffset: new google.maps.Size(0, -32) }}
          >
            {renderHoverHeader(hoverLocation)}
          </InfoWindow>
        )}


        {selectedLocation && isMobile &&
          (selectedLocation.lat && selectedLocation.lng) && (
            <Marker
              key={`selected-${getLocationId(selectedLocation).id}`}
              position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
              icon={getMarkerIcon(selectedLocation)}
              zIndex={1000}
              optimized={false}
            />
          )}
        {selectedLocation && !isMobile && selectedLocation.lat && selectedLocation.lng && (
          <Marker
            key={`selected-${getLocationId(selectedLocation).id}`}
            position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
            icon={getMarkerIcon(selectedLocation)}
            zIndex={1000}
            optimized={false}
          />
        )}


        {selectedLocation && !isMobile && selectedLocation.lat && selectedLocation.lng && (
          <InfoWindow
            position={{
              lat: selectedLocation.lat,
              lng: selectedLocation.lng
            }}
            onCloseClick={() => setSelectedLocation(null)}
            options={{
              maxWidth: 400,
              pixelOffset: new google.maps.Size(0, -20),
              disableAutoPan: true
            }}
          >
            <div className="max-w-md">
              <LocationDetails location={selectedLocation} />
            </div>
          </InfoWindow>
        )}


        {showNeighborhoods && polygons.map((poly, i) => (
          <Polygon
            key={`poly-${i}`}
            paths={poly.paths}
            options={{
              fillColor: '#2bb3ff',   // light fill
              fillOpacity: 0.08,
              strokeColor: '#004a99', // darker border
              strokeOpacity: 1,
              strokeWeight: 2
            }}
            onClick={(e) => {
              e.domEvent.stopPropagation();
              setPolyInfo({ opcoId: poly.opcoId, name: poly.name, position: e.latLng });
            }}
          />
        ))}
        {polyInfo && (
          <InfoWindow
            position={polyInfo.position}
            onCloseClick={() => setPolyInfo(null)}
            options={{ pixelOffset: new google.maps.Size(0, -20) }}
          >

            <div className="justify-center pb-1 pr-1 pl-1" >
              <div className="justify-start text-fluid-sm py-2" >
                SYW Neighborhood:
                {polyInfo.name}
              </div>


            </div>
          </InfoWindow>
        )}
      </GoogleMap>



      {/* ── Desktop: “Search this area” pill ───────────────────────── */}
      {!isMobile && showSearchHereTag && mapCenter && (
        <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 z-30">
          <button
            onClick={() => {
              searchByLatLng(mapCenter.lat, mapCenter.lng);
              setGlobalSearchMode('Nearby Search');
              setShowSearchHereTag(false);
            }}
            className="pointer-events-auto flex items-center gap-2
                        px-5 py-2 rounded-full bg-white border border-blue-200
                        shadow-md text-fluid-base font-medium text-blue-700
                        hover:bg-blue-50 transition"
          >
            <Search className="h-[18px] w-[18px]" />
            Search this area
          </button>
        </div>
      )}
      {!isMobile && deviceLocation && (
        <button
          onClick={() => {
            attemptGeo(
              pos => {
                const fix = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() };
                setDeviceLocation(fix); setGlobalDeviceLocation(fix);
                map?.panTo(fix); map?.setZoom(15);
                searchByLatLng(fix.lat, fix.lng);
                setIsOffFromDeviceLocation(false);
                setShowSearchHereTag(false);
                setGlobalSearchMode('Nearby Search');
              },
              () => {
                const fix = { ...defaultCenter, _fallback: true, ts: Date.now() };
                setDeviceLocation(fix); setGlobalDeviceLocation(fix);
                map?.panTo(fix); map?.setZoom(15);
                alert('Location unavailable – showing default area.');
              }
            );
          }}
          title="Re‑center on my location"
          className="fixed right-6 bottom-[13%] z-30
                    h-12 w-12 rounded-full bg-white border border-gray-300
                    shadow-lg flex items-center justify-center
                    hover:bg-gray-100 transition"
        >
          <LocateIcon className="h-6 w-6 text-gray-700" />
        </button>
      )}



    </div>
  );
};

export default Map;